// web/src/player/Captions.tsx
import type { JSX } from "react";
import type { VisibleWord, Mode, AspectRatio } from "./types";
import { redactWord, isRedacted } from "./profanity";
import type { CaptionMask, BleepMode } from "@/config/censorship";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE } from "@/config/censorship";

const HIGHLIGHT: Record<Mode, string> = { roast: "#E62D23", toast: "#5382FF" };
// Solid redaction bar for captionMask="box" on non-highlighted profane words. Dark grey
// (not black) so the bar stays visible over black backdrops / letterbox bars.
// MUST stay in sync with REDACT_INK in capture/captionCanvas.ts (hand-synced renderers).
const REDACT_INK = "#3a3a3a";

export function Captions({
  words,
  mode,
  highlightActive = true,
  aspect,
  captionMask = DEFAULT_CAPTION_MASK,
  bleepMode = DEFAULT_BLEEP_MODE,
}: {
  words: VisibleWord[];
  mode: Mode;
  highlightActive?: boolean;
  aspect?: AspectRatio;
  captionMask?: CaptionMask;
  bleepMode?: BleepMode;
}): JSX.Element {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-6 text-center"
      aria-live="off"
    >
      {words.map((w) => {
        const isCurrent = highlightActive && w.state === "current";
        // Box mask: profane words become a solid rounded bar — text hidden, no outline
        // (a stroke on transparent text would trace the glyphs and leak the word shape).
        const redacted = captionMask === "box" && isRedacted(w.word, bleepMode);
        return (
          <span
            key={w.index}
            style={{
              color: redacted ? "transparent" : "#FFFFFF",
              fontWeight: 700,
              opacity: 1,
              padding: "6px",
              borderRadius: "4px",
              backgroundColor: isCurrent ? HIGHLIGHT[mode] : redacted ? REDACT_INK : "transparent",
              transform: isCurrent ? "scale(1.1)" : "scale(1)",
              transformOrigin: "center center",
              transition: "none",
              display: "inline-block",
              // Non-selected words get a thick OUTLINE plus a tight, low-blur downward
              // shadow → an "extruded" look that stays legible on light backgrounds.
              // paint-order:stroke keeps the stroke behind the fill so it doesn't thin
              // the glyph. (Chrome target.) A redacted (boxed) word gets NEITHER — the
              // solid bar is the whole point, and a stroke would reveal the letters.
              ...(redacted || isCurrent
                ? { textShadow: "none" }
                : { WebkitTextStroke: "3px rgba(0,0,0,0.95)", paintOrder: "stroke", textShadow: "0 2px 1px rgba(0,0,0,1)" }),
              // 16:9 captions run 1.32x the base (wide-frame decks/photos read at a distance).
              // The base itself scales with the stage (stage font-size = min(16px, …cqh) in
              // stageStyle), so this stays proportional in the small StudioCreate preview too.
              ...(aspect === "16:9" ? { fontSize: "1.32em" } : {}),
            }}
          >
            {redactWord(w.word, captionMask, bleepMode)}
          </span>
        );
      })}
    </div>
  );
}
